import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import useFeatureStore from './store/featureStore'
import { useEffect, useState, lazy, Suspense, Component } from 'react'
import toast from 'react-hot-toast'
import logger from './utils/logger'
import { Sentry } from './sentry'
import { usePolling } from './hooks/usePolling'

import Layout from './components/Layout'
import DefaultRedirect from './components/DefaultRedirect'
import DesktopOnly from './components/DesktopOnly'
import LoadingSpinner from './components/LoadingSpinner'
import LimitExceededModal from './components/LimitExceededModal'
import UpdateAnnouncementModal from './components/UpdateAnnouncementModal'
import RecoverySubscriptionModal from './components/RecoverySubscriptionModal'
import SupportChatWidget from './components/SupportChatWidget'
import CookieBanner from './components/CookieBanner'
import { CookieConsentProvider } from './contexts/CookieConsentContext'
import useLimitHandler from './hooks/useLimitHandler'
import { useTrialStore } from './store/trialStore'
import api from './utils/api'

// Lazy-loaded страницы для оптимизации начальной загрузки
const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const Training = lazy(() => import('./pages/Training'))
const Welcome = lazy(() => import('./pages/Welcome'))
const Spy = lazy(() => import('./pages/Spy'))
const Profiles = lazy(() => import('./pages/Profiles'))
const Collections = lazy(() => import('./pages/Collections'))
const Content = lazy(() => import('./pages/Content'))
const AI = lazy(() => import('./pages/AI'))
const AIChat = lazy(() => import('./pages/AIChat'))
const VideoAnalysis = lazy(() => import('./pages/VideoAnalysis'))
const Editor = lazy(() => import('./pages/Editor'))
const ReferenceBoard = lazy(() => import('./pages/ReferenceBoard'))
const IdeasLab = lazy(() => import('./pages/IdeasLab'))
const Lenta = lazy(() => import('./pages/Lenta'))
const Monitoring = lazy(() => import('./pages/Monitoring'))
const Settings = lazy(() => import('./pages/Settings'))
const Subscription = lazy(() => import('./pages/Subscription'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const PaymentFailure = lazy(() => import('./pages/PaymentFailure'))
const Admin = lazy(() => import('./pages/Admin'))
const AdminSubscribers = lazy(() => import('./pages/AdminSubscribers'))
const AdminPayments = lazy(() => import('./pages/AdminPayments'))
const AdminUsersFunnel = lazy(() => import('./pages/AdminUsersFunnel'))
const AdminAcquisition = lazy(() => import('./pages/AdminAcquisition'))
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements'))
const AdminUpdateAnnouncements = lazy(() => import('./pages/AdminUpdateAnnouncements'))
const AdminReferrals = lazy(() => import('./pages/AdminReferrals'))
const AdminPromoCodes = lazy(() => import('./pages/AdminPromoCodes'))
const AdminFeedCategories = lazy(() => import('./pages/AdminFeedCategories'))
const AdminCampaigns = lazy(() => import('./pages/AdminCampaigns'))
const AdminSupport = lazy(() => import('./pages/AdminSupport'))
const AdminCallRequests = lazy(() => import('./pages/AdminCallRequests'))
const AdminApify = lazy(() => import('./pages/AdminApify'))
const AdminActivityLogs = lazy(() => import('./pages/AdminActivityLogs'))
const AdminOnboarding = lazy(() => import('./pages/AdminOnboarding'))
const Features = lazy(() => import('./pages/Features'))
const Referral = lazy(() => import('./pages/Referral'))
const Terms = lazy(() => import('./pages/Terms'))
const Privacy = lazy(() => import('./pages/Privacy'))
const PersonalData = lazy(() => import('./pages/PersonalData'))
const Cookies = lazy(() => import('./pages/Cookies'))
const Offer = lazy(() => import('./pages/Offer'))
const ConsentFull = lazy(() => import('./pages/ConsentFull'))
const ConsentBasic = lazy(() => import('./pages/ConsentBasic'))
const ConsentAnalytics = lazy(() => import('./pages/ConsentAnalytics'))
const ConsentMarketing = lazy(() => import('./pages/ConsentMarketing'))
const AuthSuccess = lazy(() => import('./pages/AuthSuccess'))

// Onboarding pages
const Step1PasteLink = lazy(() => import('./pages/Onboarding/Step1PasteLink'))
const Step2ProfileMetrics = lazy(() => import('./pages/Onboarding/Step2ProfileMetrics'))
const Step3AIAnalysis = lazy(() => import('./pages/Onboarding/Step3AIAnalysis'))
const Step4Dashboard = lazy(() => import('./pages/Onboarding/Step4Dashboard'))
const Step5Pricing = lazy(() => import('./pages/Onboarding/Step5Pricing'))
const OnboardingComplete = lazy(() => import('./pages/Onboarding/OnboardingComplete'))
const OAuthConsent = lazy(() => import('./pages/OAuthConsent'))
const Integrations = lazy(() => import('./pages/Integrations'))

// Компонент загрузки для Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
)

// Error Boundary — два разных кейса:
//   1. chunk-ошибка после деплоя (старый bundle тянет несуществующий чанк) →
//      overlay "Обновление приложения" + автоперезагрузка
//   2. произвольная JS-ошибка в компоненте → overlay "Что-то пошло не так"
//      БЕЗ автоперезагрузки (она бесполезна — после релоада упадёт снова)
// Раньше второй кейс ошибочно показывал тот же "Обновление приложения" —
// юзер думал что нужен релоад, а реальная ошибка пряталась в console.
const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Loading chunk',
  'Loading CSS chunk',
  // Mobile Safari iOS throws a DIFFERENT message for a stale dynamic-import chunk
  // after a deploy — without these it fell into the generic "Что-то пошло не так"
  // overlay with no auto-reload (issues CONTENTBOARD-FRONTEND-1 / -3).
  'Importing a module script failed',
  'error loading dynamically imported module',
]

function isChunkLoadError(error) {
  const msg = error?.message || ''
  return CHUNK_ERROR_PATTERNS.some(p => msg.includes(p))
}

class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, isChunkError: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) }
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      // Chunk error — пробуем перезагрузить (с throttling чтобы не зациклить)
      const lastReload = sessionStorage.getItem('chunk_reload')
      const now = Date.now()
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem('chunk_reload', String(now))
        window.location.reload()
      }
    } else {
      // Любая другая ошибка — НЕ перезагружаем (релоад на тех же
      // данных упадёт снова), отправляем в Sentry для диагностики.
      console.error('[ChunkErrorBoundary] Uncaught render error:', error, errorInfo)
      try {
        Sentry.captureException(error, {
          tags: { source: 'ChunkErrorBoundary' },
          contexts: {
            react: {
              componentStack: errorInfo?.componentStack,
            },
          },
        })
      } catch {
        // Sentry мог не инициализироваться (dev режим / DSN не задан).
        // Не блокируем рендер fallback overlay.
      }
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isChunkError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="text-center">
            <p className="text-lg font-semibold text-slate-900 mb-2">Обновление приложения</p>
            <p className="text-sm text-slate-500 mb-4">Вышла новая версия. Перезагрузите страницу.</p>
            <button
              onClick={this.handleReload}
              className="px-6 py-2 bg-brand-lime text-brand-black rounded-lg font-semibold hover:bg-brand-lime-600 transition"
            >
              Перезагрузить
            </button>
          </div>
        </div>
      )
    }

    // Generic JS error — перезагрузка тут бесполезна (после релоада упадёт
    // на тех же данных). Даём юзеру попробовать, но честно говорим что
    // именно произошло.
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-md">
          <p className="text-lg font-semibold text-slate-900 mb-2">Что-то пошло не так</p>
          <p className="text-sm text-slate-500 mb-4">
            Произошла ошибка при отображении страницы. Попробуйте перезагрузить или вернуться назад.
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={this.handleReload}
              className="px-6 py-2 bg-brand-lime text-brand-black rounded-lg font-semibold hover:bg-brand-lime-600 transition"
            >
              Перезагрузить
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg font-semibold hover:bg-slate-200 transition"
            >
              Назад
            </button>
          </div>
        </div>
      </div>
    )
  }
}

function App() {
  const { user, isLoading, checkAuth } = useAuthStore()
  const { limitModalOpen, limitType, closeLimitModal } = useLimitHandler()
  const loadFeatures = useFeatureStore(state => state.loadFeatures)
  const { lockState, fetchLockState } = useTrialStore()
  const [announcement, setAnnouncement] = useState(null)

  useEffect(() => {
    checkAuth()
    // Capture acquisition data (UTM + referrer) on first visit so it survives
    // the landing → signup navigation via localStorage.
    import('./utils/acquisition').then(m => m.captureAcquisitionOnce())
  }, [checkAuth])

  // Sync user-context в Sentry — чтобы в error-репортах было видно
  // who-was-affected. Sentry.setUser(null) при логауте — чистит контекст
  // (не репортить новые ошибки с предыдущим юзером).
  useEffect(() => {
    try {
      if (user) {
        Sentry.setUser({
          id: user._id || user.id,
          email: user.email,
          username: user.firstName || user.email,
        })
      } else {
        Sentry.setUser(null)
      }
    } catch {
      // Sentry не инициализирован (dev mode) — игнорим
    }
  }, [user])

  // Загружаем feature flags при логине + раз в 60 сек чтобы подхватывать
  // изменения из админки. usePolling пропускает tick когда юзер offline
  // и делает exponential backoff на consecutive failures — раньше
  // setInterval бил каждые 60с даже без сети, плодя зависшие requests.
  usePolling(loadFeatures, 60000, { enabled: !!user, runImmediately: true })

  // Подтягиваем trial/lock-state при логине — нужно для TrialCountdownBanner,
  // ViewOnlyLockHero и RecoverySubscriptionModal
  useEffect(() => {
    if (user) fetchLockState()
  }, [user, fetchLockState])

  // Загружаем активное объявление при входе (только если тур уже пройден)
  useEffect(() => {
    if (user && user.onboardingCompletedAt) {
      fetchActiveAnnouncement()
    }
  }, [user])

  // Слушаем событие завершения тура, чтобы показать объявление после
  useEffect(() => {
    const handleOnboardingCompleted = () => {
      if (user) {
        fetchActiveAnnouncement()
      }
    }

    window.addEventListener('onboarding-completed', handleOnboardingCompleted)
    return () => {
      window.removeEventListener('onboarding-completed', handleOnboardingCompleted)
    }
  }, [user])

  // Показываем тост-предупреждение при приближении к дневному лимиту trial.
  // api.js диспатчит 'trial-usage-warning' когда бэкенд присылает заголовок X-Trial-Warning (80% дневного кэпа).
  useEffect(() => {
    const handler = (e) => {
      const raw = e.detail?.message || ''
      // Бэкенд шлёт что-то вроде "video usage at 80%" — переводим в дружелюбный русский текст.
      const pctMatch = raw.match(/at\s+(\d+)\s*%/i)
      const msg = pctMatch
        ? `Использовано ${pctMatch[1]}% дневного лимита trial`
        : (raw || 'Приближаешься к дневному лимиту trial')

      toast(msg, {
        id: 'trial-warning', // дедуп: повторные срабатывания заменяют тост, а не плодят новые
        icon: '⚠️',
        duration: 5000,
        style: { background: '#1a1a1a', color: '#d4ff00', border: '1px solid #d4ff00' }
      })
    }

    window.addEventListener('trial-usage-warning', handler)
    return () => window.removeEventListener('trial-usage-warning', handler)
  }, [])

  const fetchActiveAnnouncement = async () => {
    try {
      const response = await api.get('/update-announcements/active')
      if (response.data.data) {
        setAnnouncement(response.data.data)
      }
    } catch (error) {
      // Игнорируем ошибки - модалка необязательна
      logger.error('Failed to fetch announcement:', error)
    }
  }

  const handleAnnouncementView = async () => {
    if (!announcement) return

    try {
      await api.post(`/update-announcements/${announcement._id}/view`)
    } catch (error) {
      logger.error('Failed to mark announcement as viewed:', error)
    }
  }

  const handleAnnouncementClick = async () => {
    if (!announcement) return

    try {
      await api.post(`/update-announcements/${announcement._id}/click`)
    } catch (error) {
      logger.error('Failed to register announcement click:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <CookieConsentProvider>
        <ChunkErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/personal-data" element={<PersonalData />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/offer" element={<Offer />} />
            <Route path="/consent-full" element={<ConsentFull />} />
            <Route path="/consent-basic" element={<ConsentBasic />} />
            <Route path="/consent-analytics" element={<ConsentAnalytics />} />
            <Route path="/consent-marketing" element={<ConsentMarketing />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        <CookieBanner />
      </CookieConsentProvider>
    )
  }

  // Subtle visual cue: dim dashboard when account is in view-only lock
  const lockedClass = lockState === 'view_only' ? 'opacity-90' : ''

  return (
    <CookieConsentProvider>
      <div className={lockedClass}>
      <Routes>
        {/* Onboarding — authenticated but no Layout */}
        <Route path="/onboarding/step-1" element={<Suspense fallback={<PageLoader />}><Step1PasteLink /></Suspense>} />
        <Route path="/onboarding/step-2" element={<Suspense fallback={<PageLoader />}><Step2ProfileMetrics /></Suspense>} />
        <Route path="/onboarding/step-3" element={<Suspense fallback={<PageLoader />}><Step3AIAnalysis /></Suspense>} />
        <Route path="/onboarding/step-4" element={<Suspense fallback={<PageLoader />}><Step4Dashboard /></Suspense>} />
        <Route path="/onboarding/step-5" element={<Suspense fallback={<PageLoader />}><Step5Pricing /></Suspense>} />
        <Route path="/onboarding/complete" element={<Suspense fallback={<PageLoader />}><OnboardingComplete /></Suspense>} />

        {/* OAuth consent — authenticated, без Layout. Путь /integrations/consent
            (не /oauth/consent), чтобы host nginx мог чисто разделить:
            /oauth/* → backend (Express), /integrations/* → frontend (SPA). */}
        <Route path="/integrations/consent" element={<Suspense fallback={<PageLoader />}><OAuthConsent /></Suspense>} />

        {/* All other routes — wrapped in Layout */}
        <Route path="*" element={
          <Layout>
            <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<DefaultRedirect />} />
                <Route path="/training" element={<Training />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/dashboard" element={<Content />} />
                <Route path="/content" element={<Navigate to="/dashboard" />} />
                <Route path="/spy" element={<Spy />} />
                <Route path="/profiles" element={<Profiles />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/ai" element={<DesktopOnly title="Воркспейс — только на компьютере" description="Здесь ты работаешь с бесконечным холстом: тащишь видео, собираешь доски, подключаешь AI-блоки. На телефоне это неудобно — открой на компе."><AI /></DesktopOnly>} />
                <Route path="/ai-chat" element={<AIChat />} />
                <Route path="/profile-analysis" element={<Navigate to="/video-analysis?tab=profile" />} />
                <Route path="/video-analysis" element={<VideoAnalysis />} />
                <Route path="/editor" element={<Editor />} />
                <Route path="/ai/boards/:id" element={<DesktopOnly title="Доска — только на компьютере" description="Доска использует перетаскивание, ресайз и зум — это создано под мышку и большой экран. Открой на компе, чтобы продолжить."><ReferenceBoard /></DesktopOnly>} />
                <Route path="/ideas-lab" element={<IdeasLab />} />
                <Route path="/lenta" element={<Lenta />} />
                <Route path="/monitoring" element={<Monitoring />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/subscription/success" element={<PaymentSuccess />} />
                <Route path="/subscription/failure" element={<PaymentFailure />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/subscribers" element={<AdminSubscribers />} />
                <Route path="/admin/users" element={<AdminUsersFunnel />} />
                <Route path="/admin/payments" element={<AdminPayments />} />
                <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                <Route path="/admin/update-announcements" element={<AdminUpdateAnnouncements />} />
                <Route path="/admin/referrals" element={<AdminReferrals />} />
                <Route path="/admin/acquisition" element={<AdminAcquisition />} />
                {/* AdminUsage removed — merged into Journal (AdminActivityLogs) */}
                <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
                <Route path="/admin/feed" element={<AdminFeedCategories />} />
                <Route path="/admin/campaigns" element={<AdminCampaigns />} />
                <Route path="/admin/support" element={<AdminSupport />} />
                <Route path="/admin/call-requests" element={<AdminCallRequests />} />
                <Route path="/admin/apify" element={<AdminApify />} />
                <Route path="/admin/activity" element={<AdminActivityLogs />} />
                <Route path="/admin/onboarding" element={<AdminOnboarding />} />
                <Route path="/admin/features" element={<Features />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/referral" element={<Referral />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/personal-data" element={<PersonalData />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/offer" element={<Offer />} />
                <Route path="/consent-full" element={<ConsentFull />} />
                <Route path="/consent-basic" element={<ConsentBasic />} />
                <Route path="/consent-analytics" element={<ConsentAnalytics />} />
                <Route path="/consent-marketing" element={<ConsentMarketing />} />
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Suspense>
            </ChunkErrorBoundary>
          </Layout>
        } />
      </Routes>
      </div>

      {/* Global Recovery Subscription Modal — listens for 'account-locked' window event
          dispatched by api.js interceptor on 423 LOCKED responses */}
      <RecoverySubscriptionModal />

      {/* Global Limit Exceeded Modal */}
      <LimitExceededModal
        isOpen={limitModalOpen}
        onClose={closeLimitModal}
        limitType={limitType}
      />

      {/* Update Announcement Modal - только для существующих пользователей (зарегались до объявления) */}
      {announcement && user?.onboardingCompletedAt && user?.createdAt &&
       new Date(user.createdAt) < new Date(announcement.createdAt) && (
        <UpdateAnnouncementModal
          announcement={announcement}
          onClose={() => setAnnouncement(null)}
          onView={handleAnnouncementView}
          onActionClick={handleAnnouncementClick}
        />
      )}

      {/* Support Chat Widget */}
      {user && <SupportChatWidget />}

      {/* Cookie Banner */}
      <CookieBanner />
    </CookieConsentProvider>
  )
}

export default App